// Per-site Guest Experience rating configuration.
//
// A site chooses which criteria it asks about (experienceCriteria) and how many
// stars each is scored out of (experienceStarCount). Both default to what the
// app has always used, so a site with nothing configured behaves exactly as
// before.
//
// IMPORTANT — this file is duplicated in the guest rating app
// (application _experience/src/constants/experienceCriteria.js). The two builds
// are deployed separately and share no code, so any change here must be applied
// there as well or the two will disagree about how to score a review.

export const DEFAULT_EXPERIENCE_CRITERIA = [
  { key: 'staffRating', label: 'Efficiency and Friendliness of the Staff' },
  { key: 'barService', label: 'Bar Efficiency' },
  { key: 'waiterService', label: 'Waiter Efficiency' },
  { key: 'breakfastRating', label: 'Breakfast Dining Experience' },
  { key: 'lunchRating', label: 'Lunch Dining Experience' },
  { key: 'dinnerRating', label: 'Dinner Dining Experience' },
  { key: 'mainAreaComfort', label: 'Comfort and Cleanliness (Main Areas)' },
  { key: 'inRoomExperience', label: 'In-room Experience' },
  { key: 'roomCleanliness', label: 'Room Cleanliness' },
  { key: 'spaExperience', label: 'Spa Experience' }
];

export const DEFAULT_STAR_COUNT = 4;
export const MIN_STAR_COUNT = 3;
export const MAX_STAR_COUNT = 10;

const clampScale = value => {
  const n = Number(value);
  return Number.isFinite(n) && n >= MIN_STAR_COUNT && n <= MAX_STAR_COUNT
    ? n
    : DEFAULT_STAR_COUNT;
};

// ---------------------------------------------------------------------------
// Reading configuration off a site
// ---------------------------------------------------------------------------

export const resolveStarCount = site => clampScale(site && site.experienceStarCount);

export const resolveSiteCriteria = site => {
  const configured = site && site.experienceCriteria;
  return Array.isArray(configured) && configured.length
    ? configured.filter(c => c && c.key && c.label)
    : DEFAULT_EXPERIENCE_CRITERIA;
};

// ---------------------------------------------------------------------------
// Reading a stored review
// ---------------------------------------------------------------------------

// Reviews stamp the scale they were captured on. Without it we'd reinterpret
// history every time a site changed its star count — a 4/4 becoming a 4/5.
// Rows written before that existed are 4-star by definition.
export const scaleOfRecord = record => clampScale(record && record.ratingScale);

// Which criteria a given review actually answered. Older rows predate the
// stamp, so they used the fixed default set.
export const criteriaKeysOfRecord = record => {
  const stamped = record && record.criteriaKeys;
  return Array.isArray(stamped) && stamped.length
    ? stamped
    : DEFAULT_EXPERIENCE_CRITERIA.map(c => c.key);
};

// ---------------------------------------------------------------------------
// Scoring
// ---------------------------------------------------------------------------

// N/A sits one above the top star so it can never collide with a real score.
// On the historical 4-star scale that is 5, which is what existing rows hold.
export const naValueFor = scale => clampScale(scale) + 1;

export const isNA = (value, scale) => Number(value) === naValueFor(scale);

// 0 or missing means "not answered". N/A is answered but excluded from scoring,
// so only 1..scale counts toward an average.
export const isScored = (value, scale) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 && n <= clampScale(scale);
};

// Returns null when the value shouldn't contribute to a score, so callers can
// distinguish "no score" from a genuine zero.
export const toPercent = (value, scale) => {
  const max = clampScale(scale);
  return isScored(value, max) ? (Number(value) / max) * 100 : null;
};

// ---------------------------------------------------------------------------
// Editing configuration
// ---------------------------------------------------------------------------

// Build a stable key from a label. Generated once when a criterion is created
// and then left alone — renaming a criterion must not orphan its history.
export const makeCriterionKey = (label, existingKeys = []) => {
  const words = String(label || '')
    .replace(/[^a-zA-Z0-9 ]/g, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);

  const base =
    words
      .map((word, index) =>
        index === 0
          ? word.toLowerCase()
          : word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
      )
      .join('') || 'criterion';

  let key = base;
  let suffix = 2;
  while (existingKeys.includes(key)) {
    key = `${base}${suffix}`;
    suffix += 1;
  }
  return key;
};

// ---------------------------------------------------------------------------
// Display
// ---------------------------------------------------------------------------

// The criteria to show for a site: everything currently configured, plus any
// criterion that reviews were actually collected under. Retired ones are
// flagged rather than dropped, so changing a site's criteria never makes past
// scores look like they vanished.
export const criteriaWithHistory = (site, records = []) => {
  const configured = resolveSiteCriteria(site);
  const seen = new Set(configured.map(c => c.key));
  const result = configured.map(c => ({ ...c, retired: false }));

  // Best-effort labels for retired keys: a site's own config wins, otherwise
  // fall back to the built-in defaults, otherwise show the raw key.
  const labels = {};
  DEFAULT_EXPERIENCE_CRITERIA.forEach(c => {
    labels[c.key] = c.label;
  });
  configured.forEach(c => {
    labels[c.key] = c.label;
  });

  (records || []).forEach(record => {
    criteriaKeysOfRecord(record).forEach(key => {
      if (!seen.has(key)) {
        seen.add(key);
        result.push({ key, label: labels[key] || key, retired: true });
      }
    });
  });

  return result;
};
